import { FlashProfile } from "./FlashProfile";
import { buildMinimalSFDP, WINBOND_W25Q64_SFDP } from "./sfdpFixtures";

/**
 * Build the default list of demo flash profiles. Each entry represents a
 * distinct behaviour the UI / firmware stack needs to cope with — from a
 * standard modern chip with SFDP to a dead wire that just reads 0xFF.
 *
 * Profiles are created fresh on each call so tests can tweak state
 * without mutating shared fixtures.
 */
function createProfiles() {
    return [
        new FlashProfile({
            id: "winbond_w25q64",
            name: "Winbond W25Q64 — 8 MB, SFDP, 3-byte",
            description: "Standard modern chip: RDID, full SFDP, 3-byte addressing.",
            rdid: new Uint8Array([0xEF, 0x70, 0x17]),
            sfdp: WINBOND_W25Q64_SFDP,
            capacity: 8 * 1024 * 1024,
            addressBytes: 3,
        }),

        new FlashProfile({
            id: "winbond_w25q256",
            name: "Winbond W25Q256 — 32 MB, SFDP, 4-byte opcodes",
            description: "Large flash needing 4-byte addresses. Like the real part, SFDP reports \"3- or 4-byte, default 3\" and powers up in 3-byte mode; dedicated 4-byte opcodes are listed in the 4-byte address instruction table.",
            rdid: new Uint8Array([0xEF, 0x70, 0x19]),
            sfdp: buildMinimalSFDP({
                densityBytes: 32 * 1024 * 1024,
                addressBytesField: 0b01,
                enter4ByteMethods: 0b0100001,   // B7h, dedicated 4-byte opcodes
                exit4ByteMethods: 0b0000001,    // E9h
                fourByteTable: { read: true, program: true, eraseOpcodes: { 1: 0x21, 3: 0xDC } },
            }),
            capacity: 32 * 1024 * 1024,
            addressBytes: 3,
        }),

        new FlashProfile({
            id: "en4b_only_32mb",
            name: "32 MB, EN4B only — no 4-byte opcodes",
            description: "Large flash without dedicated 4-byte opcodes (like the old MX25L25635E). Must be switched into 4-byte mode with B7h, and back with E9h.",
            rdid: new Uint8Array([0xC2, 0x20, 0x19]),
            sfdp: buildMinimalSFDP({
                densityBytes: 32 * 1024 * 1024,
                addressBytesField: 0b01,
                enter4ByteMethods: 0b0000001,   // B7h
                exit4ByteMethods: 0b0000001,    // E9h
            }),
            capacity: 32 * 1024 * 1024,
            addressBytes: 3,
            supportsFourByteOpcodes: false,
        }),

        new FlashProfile({
            id: "macronix_mx25l1606",
            name: "Macronix MX25L1606E — 2 MB, SFDP, 3-byte",
            description: "Mid-size Macronix chip with SFDP.",
            rdid: new Uint8Array([0xC2, 0x20, 0x15]),
            sfdp: buildMinimalSFDP({
                densityBytes: 2 * 1024 * 1024,
                addressBytes: 3,
            }),
            capacity: 2 * 1024 * 1024,
            addressBytes: 3,
        }),

        new FlashProfile({
            id: "sst_no_sfdp",
            name: "SST 1 MB — RDID only, no SFDP",
            description: "Older chip: responds to 9F, but no SFDP table. Capacity derived from RDID byte.",
            rdid: new Uint8Array([0xBF, 0x25, 0x14]),
            sfdp: null,
            capacity: 1 * 1024 * 1024,
            addressBytes: 3,
        }),

        new FlashProfile({
            id: "unknown_vendor_no_sfdp",
            name: "Unknown vendor 2 MB — RDID only",
            description: "Unknown manufacturer ID, but a sensible capacity byte.",
            rdid: new Uint8Array([0x12, 0x34, 0x15]),
            sfdp: null,
            capacity: 2 * 1024 * 1024,
            addressBytes: 3,
        }),

        new FlashProfile({
            id: "bad_rdid_no_sfdp",
            name: "Bad RDID capacity — manual size required",
            description: "Vendor ID OK, but the capacity byte is out of the usual range, so detection must fall through to a manual override.",
            rdid: new Uint8Array([0xEF, 0x70, 0x05]),
            sfdp: null,
            capacity: 1 * 1024 * 1024,
            addressBytes: 3,
        }),

        new FlashProfile({
            id: "unresponsive_ff",
            name: "Unresponsive — MISO stuck high",
            description: "Chip not powered / not present: every read returns 0xFF.",
            rdid: new Uint8Array([0xFF, 0xFF, 0xFF]),
            sfdp: null,
            capacity: 1 * 1024 * 1024,
            addressBytes: 3,
        }),

        new FlashProfile({
            id: "dead_zeros",
            name: "Dead line — MISO stuck low",
            description: "Wiring fault / shorted line: every read returns 0x00.",
            // 00 00 00 is what the existing RDID guard considers 'null', so
            // we use it to exercise that path.
            rdid: new Uint8Array([0x00, 0x00, 0x00]),
            sfdp: null,
            capacity: 1 * 1024 * 1024,
            addressBytes: 3,
        }),
    ];
}

/**
 * Lookup a profile by its `id`. Returns undefined if not found so callers
 * can decide how to handle the miss (e.g. fall back to the default).
 */
function findProfile(profiles, id) {
    return profiles.find(p => p.id === id);
}

export { createProfiles, findProfile };
